import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import TeamService from "services/TeamService";
import { useTeamContext } from "store/contexts/teamContext";
import { useTimelineContext } from "store/contexts/timelineContext";
import { useNewPostContext } from "store/contexts/newPostContext";
import CustomTextArea from "components/CustomTextArea";
import GeneralService from "services/GeneralService";
import ColorSelection from "components/ColorSelection";
import AttachmentPreview from "components/AttachmentPreview/AttachmentPreview";
import AttachmentSelection from "components/AttachmentSelection/AttachmentSelection";
import ButtonWithProgress from "components/ButtonWithProgress";
import { useToasts } from "react-toast-notifications";
import { useGlobalContext } from "store/contexts/globalContext";
import { useCreatePostContext } from "store/contexts/createPostContext";
import ChatGptAssistModal from "components/ChatGptAssistModal";
import AIOptions from "components/AI-Assistant/AIOptions";
import { AIContext } from "store/contexts/aiContext";
import GenerateCaptions from "components/AI-Assistant/GenerateCaptions";
import { useTeamActionContext } from "store/contexts/teamActionContext";

export default () => {
  const { team } = useTeamContext();
  const { handleClose, sOption } = useCreatePostContext();
  const { microsoftTeamsVideoText } = useGlobalContext();
  const { createPost } = useTimelineContext();
  const { createNewPost } = useTeamActionContext();
  const { postColors } = useNewPostContext();
  const [selectedColor, setSelectedColor] = useState(postColors[0]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [aiOption, setAiOption] = useState(0);
  const [displayAIModal, setDisplayAI] = useState(false);
  const [reSubmit, setResubmit] = useState(false);
  const { addToast } = useToasts();
  const buttonOptions = {
    1: "Generate Avatar",
    2: "Generate Summary",
  };

  useEffect(() => {
    setSelectedColor(postColors[0]);
  }, [postColors]);

  const submitForm = async (values, summary = null) => {
    let media = null;

    if (aiOption) {
      if (!values.text)
        return setError("Please generate caption or write text to continue");

      if (aiOption === 1 && values.text && values.text.length < 30)
        return setError(
          "To generate Avatar text should be 30 to 500 characters"
        );

      if (aiOption === 1 && values.text && values.text.length > 500)
        return setError(
          "To generate Avatar text should be 30 to 500 characters"
        );

      if (aiOption === 2 && values.text && values.text.length < 50) {
        return setError(
          "To generate Summary text should be 50 to 5000 characters."
        );
      }

      if (aiOption === 2 && values.text && values.text.length > 5000) {
        return setError(
          "To generate Summary text should be 50 to 5000 characters."
        );
      }

      if (aiOption === 2) setResubmit(!reSubmit);

      return setDisplayAI(true);
    }

    if (values?.text && (values?.text.length < 4 || values?.text.length > 5000))
      return setError("Idea should be between 4 to 5000 characters");

    if (values.attachment) {
      media = await sendToS3(values.attachment, setUploadProgress);
    }

    setUploadProgress(100);

    const response = await TeamService.createBrightidea(team.id, {
      text: values.text,
      media,
      attachment: values.attachment,
      color_id: selectedColor.id.toString(),
      avatarMedia: values.avatarMedia,
      ...(summary ? { summary: summary } : {}),
    });

    response.data.data.type = "brightidea";

    if (values.attachment) {
      var attachmentType = values.attachment.type.split("/");
    }

    formik.resetForm();
    setUploadProgress(0);

    if (
      attachmentType &&
      attachmentType[0] === "video" &&
      attachmentType[1] !== "mp4" &&
      !microsoftTeamsVideoText
    ) {
      handleClose();
      addToast(
        "Your post is being processed. We’ll let you know when it’s ready to view. ",
        { appearance: "info" }
      );
      return;
    }

    createPost(response.data.data);
    handleClose();
  };

  const formik = useFormik({
    initialValues: {
      text: "",
      attachment: null,
      avatarMedia: "",
    },
    validateOnBlur: false,
    validateOnMount: false,
    validateOnChange: false,
    validationSchema: Yup.object().shape(
      {
        text: Yup.string().when(["attachment"], {
          is: (attachment) => {
            return !attachment;
          },
          then: Yup.string()
            .min(4)
            .max(5000, "Idea should not be greater then 5000")
            .required("Please enter the message or attach the media"),
        }),
        attachment: Yup.mixed().when(["text"], {
          is: (text) => {
            return !text;
          },
          then: Yup.mixed().required(
            "Please enter the message or attach the media"
          ),
        }),
      },
      ["attachment", "text"]
    ),
    onSubmit: async (values) => {
      try {
        await submitForm(values, null);
      } catch (e) {
        console.log(e);
      }
    },
  });

  const sendToS3 = async (attachment, setProgressBar) => {
    var media = null;

    try {
      const preSignedResponse = await GeneralService.getPresignedUploadURL({
        filename: attachment.name,
        mime_type: attachment.type,
        file_size: attachment.size,
        resource_type: "team",
        resource_id: team.id,
        resource_policy: "team.celebration",
      });

      const { key, url } = preSignedResponse.data.data;

      media = key;

      await GeneralService.uploadToS3(url, attachment, (progress) => {
        setProgressBar(`${Math.floor(progress)}%`);
      });

      return media;
    } catch (e) {
      console.log(e);
    }
  };

  const onAttachmentSelected = (file) => {
    formik.setFieldValue("attachment", file);
  };

  const setError = (
    value = "Text should be greater then 30 characters to use ReTeam Avatar."
  ) => {
    formik.setErrors({ text: value });
  };

  const clearAttachment = () => {
    formik.setFieldValue("attachment", null);
  };

  const setValue = (value) => {
    formik.setFieldValue("text", value);
  };

  const postSummaryOnTimeline = async (res) => {
    try {
      const response = await TeamService.createBrightidea(team.id, {
        text: formik.values.text,
        media: null,
        attachment: null,
        color_id: selectedColor.id.toString(),
        summary: res.overallSummary,
      });
      response.data.data.type = "brightidea";

      createPost(response.data.data);
      handleClose();
    } catch (e) {
      addToast("Some error occurred.", {
        appearance: "error",
      });
    }
  };

  const createTodoFromActionItems = async (res) => {
    try {
      const response = await TeamService.createTodoFromActionItems({
        teamId: team.id,
        taskName: res.listName,
        tasks: res.actionItems.map((t) => t.text),
        priority: "",
        dueDate: "",
      });

      var postData = {
        id: response.data.data.todo.id,
        content: res.listName,
      };
      createNewPost(
        postData,
        response.data.data.todo.team_post_id,
        response.data.data.todo.updated_at,
        { name: "todo-list", type_id: 16 }
      );
    } catch (e) {
      addToast("Some error occurred.", {
        appearance: "error",
      });
    }
  };

  return (
    <section style={{ width: "100%" }}>
      <AIContext.Provider
        value={{
          formik,
          setAiOption,
          aiOption,
          sendToS3,
          displayAIModal,
          setDisplayAI,
          reSubmit,
          postSummaryOnTimeline,
          createTodoFromActionItems,
          allowTodo: true,
          setError,
          sOption,
        }}
      >
        <form
          noValidate
          onSubmit={formik.handleSubmit}
          className="creatingPost createBrightIdeaPost"
        >
          <div className="textAttachmentContent">
            <div className="text-area-create-post bright-idea-create-post">
              <div className="brightIdea-text-area">
                <div className="brightIdea-text-area-root">
                  <div
                    className="brightIdea-text-area-header"
                    style={{ background: selectedColor?.gradientString }}
                  >
                    <ChatGptAssistModal setValue={setValue} />
                    <AttachmentSelection
                      videoClass="brightIdea-attachment-icon"
                      attachmentClass="brightIdea-attachment-icon"
                      onChange={onAttachmentSelected}
                      accepted={
                        aiOption
                          ? [".mkv", ".mov", ".mp4", ".mp3", ".webm"]
                          : [
                              ".png",
                              ".jpg",
                              ".jpeg",
                              ".mp4",
                              ".mp3",
                              ".webm",
                              ".pdf",
                              ".doc",
                              ".docx",
                              ".xls",
                              ".xlsx",
                              ".ppt",
                              ".pptx",
                              ".txt",
                              ".rtf",
                              ".csv",
                            ]
                      }
                      selectors={
                        !aiOption
                          ? ["audio", "image", "video", "screen"]
                          : ["audio"]
                      }
                      type={aiOption ? "audio" : "video"}
                      allowDocuments={!aiOption}
                    />
                  </div>
                  <CustomTextArea
                    parentStyle={{
                      marginBottom: 0,
                    }}
                    errorClass="textStyle"
                    textAreaClass="form-control create-text-padding create-brightidea"
                    background={selectedColor?.gradientString}
                    textColor={selectedColor?.textColor}
                    formik={formik}
                    placeholder="Share your Idea"
                    fieldName={"text"}
                    rows={3}
                    limit={500}
                  />
                </div>
                <ColorSelection
                  selectedColor={selectedColor}
                  colors={postColors}
                  setSelectedColor={setSelectedColor}
                />
              </div>
            </div>
            {!!formik.values.attachment && (
              <div className="attachment-preview-root">
                <AttachmentPreview
                  commentAttachmentPreview={"brightIdeaAttachment"}
                  attachment={formik.values.attachment}
                  clearAttachment={clearAttachment}
                />
                {aiOption && !formik.values.text && <GenerateCaptions />}
              </div>
            )}
          </div>
          <hr />
          <AIOptions />
          <hr />
          <div className="writepost-footer">
            <div className="writepost-left-btns">
              <ButtonWithProgress
                initialText={buttonOptions[aiOption] ?? "Create"}
                isSubmitting={formik.isSubmitting}
                buttonWithProgressClass="send-btn"
                progress={uploadProgress}
              />
            </div>
          </div>
        </form>
      </AIContext.Provider>
    </section>
  );
};
